import { useEffect, useState } from 'react';
import {
  api,
  type ContactDetail as Detail,
  type ContactRelationship,
  type ContactRelationshipKind,
  type MergeRow,
  type ReviewEntry,
} from '../lib/api';

/** How each link reads on the record, from the open contact's point of view. */
const RELATIONSHIP_LABEL: Record<ContactRelationshipKind, string> = {
  household: 'Same household',
  same_company: 'Same company',
  referred_by: 'Referral',
  related_party: 'Related party',
};

/**
 * Contact detail panel. Reachable from the directory.
 *
 * It shows the derived values (tier, category, entity routing) next to the
 * fields they were derived from, so a wrong tier is diagnosable on the screen
 * rather than only in the reconciliation report.
 */
export function ContactDetailPanel({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const [detail, setDetail] = useState<{
    contact: Detail;
    projects: { id: string; code: string; name: string; status: string; role: string }[];
    review: ReviewEntry[];
    merges: MergeRow[];
    relationships: ContactRelationship[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setDetail(await api.contact(id));
      } catch {
        setError('That contact could not be opened in the current entity scope.');
      }
    })();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id, onClose]);

  const contact = detail?.contact;

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-center bg-navy/60 p-4"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-detail-heading"
        className="max-h-full w-full max-w-3xl overflow-y-auto rounded-card border border-border bg-white p-5"
      >
        <header className="flex items-start justify-between gap-3">
          <div>
            <h2 id="contact-detail-heading" className="text-h1 font-semibold text-text-primary">
              {contact?.display_name ?? 'Contact'}
            </h2>
            {contact?.company_name ? (
              <p className="text-body text-text-secondary">{contact.company_name}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary rounded-card px-3 text-body font-medium"
          >
            Close
          </button>
        </header>

        {error ? (
          <p role="alert" className="mt-3 text-body text-danger">
            {error}
          </p>
        ) : null}

        {contact ? (
          <>
            <dl className="mt-4 grid gap-x-4 gap-y-2 lg:grid-cols-2">
              {[
                ['Tier', contact.tier],
                ['Category', contact.category.replace(/_/g, ' ')],
                ['Entity routing', contact.pillars.join(', ') || 'none — outside every scope'],
                ['Email', contact.email],
                ['Second email', contact.email_secondary],
                ['Phone', contact.phone_primary],
                ['Second phone', contact.phone_secondary],
                ['Website', contact.website],
                ['Address', [contact.address_line1, contact.address_line2].filter(Boolean).join(', ')],
                ['City / state', [contact.city, contact.state_region].filter(Boolean).join(', ')],
                ['Postal code', contact.postal_code],
                ['Source', [contact.source_channel, contact.source_detail].filter(Boolean).join(' · ')],
                ['Legacy ID', contact.legacy_jnid],
                ['Dedupe key', contact.dedupe_key],
                ['Import batch', contact.import_batch_id],
              ].map(([term, value]) => (
                <div key={term as string}>
                  <dt className="micro-label text-text-secondary">{term}</dt>
                  <dd
                    className={
                      term === 'Phone' || term === 'Legacy ID' || term === 'Import batch'
                        ? 'numeric break-all text-numeric'
                        : 'break-words'
                    }
                  >
                    {(value as string | null) || '—'}
                  </dd>
                </div>
              ))}
            </dl>

            {contact.notes ? (
              <div className="mt-4">
                <p className="micro-label text-text-secondary">Notes (carried from the legacy record)</p>
                <p className="mt-1 whitespace-pre-wrap rounded-card border border-border bg-surface p-3 text-body">
                  {contact.notes}
                </p>
              </div>
            ) : null}

            {detail.projects.length > 0 ? (
              <div className="mt-4">
                <p className="micro-label text-text-secondary">Projects</p>
                <ul className="mt-1 grid gap-1">
                  {detail.projects.map((project) => (
                    <li key={project.id} className="text-body">
                      <span className="numeric text-numeric">{project.code}</span> · {project.name} ·{' '}
                      {project.role}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/*
              P4-2. Relationships that run through no project. The row is
              stored once for the pair, so the server already resolved which
              half is the other person and we name only them.
            */}
            {detail.relationships.length > 0 ? (
              <div className="mt-4">
                <p className="micro-label text-text-secondary">Related contacts</p>
                <ul className="mt-1 grid gap-1">
                  {detail.relationships.map((link) => (
                    <li key={link.id} className="text-body">
                      <span className="font-semibold text-text-primary">
                        {RELATIONSHIP_LABEL[link.kind]}
                      </span>{' '}
                      · {link.other_display_name}
                      {link.other_company_name ? ` · ${link.other_company_name}` : ''}
                      {link.kind === 'referred_by'
                        ? link.referrer_id === link.other_id
                          ? ' · referred this contact'
                          : ' · referred by this contact'
                        : ''}
                      {link.evidence ? (
                        <span className="text-text-secondary"> · {link.evidence}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {detail.review.length > 0 ? (
              <div className="mt-4">
                <p className="micro-label text-text-secondary">Review queue</p>
                <ul className="mt-1 grid gap-1">
                  {detail.review.map((entry) => (
                    <li key={entry.id} className="text-body">
                      {entry.decision_key} · {entry.status} · {entry.signal}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {detail.merges.length > 0 ? (
              <div className="mt-4">
                <p className="micro-label text-text-secondary">Merge history</p>
                <ul className="mt-1 grid gap-1">
                  {detail.merges.map((merge) => (
                    <li key={merge.id} className="text-body">
                      {merge.origin.replace(/_/g, ' ')} ·{' '}
                      <span className="numeric text-numeric">{merge.loser_legacy_jnid}</span> ·{' '}
                      {merge.reason}
                      {merge.reverted_at ? ' · reverted' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {contact.merged_into_id ? (
              <p className="mt-4 rounded-card border border-warning px-3 py-2 text-body text-warning">
                This record is merged into another contact and is hidden from the directory. It is retained
                so the merge can be undone.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
